import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Plus as AddIcon,
  MoreVertical as MoreVertIcon,
  Pencil as EditIcon,
  Trash2 as DeleteIcon,
  Download as GetAppIcon,
  Eye as VisibilityIcon,
  Clock as ScheduleIcon,
  CheckCircle2 as CheckCircleIcon,
  FileText as DescriptionIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import api from '../../utils/axios';
import { HEAD_CELL_SX, TABLE_CONTAINER_SX } from '../../styles/tableStyles';

const AppealsDashboard = ({ onNewAppeal }) => {
  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAppeal, setSelectedAppeal] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [appealToDelete, setAppealToDelete] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    loadAppeals();
  }, []);

  const loadAppeals = async () => {
    try {
      setLoading(true);
      const response = await api.get('/documents/minimal-appeals/');
      const appealsData = response.data.results || response.data;
      console.log('Loaded appeals:', appealsData);
      setAppeals(appealsData);
      setError(null);
    } catch (err) {
      setError('Failed to load appeals');
      console.error('Error loading appeals:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event, appeal) => {
    setAnchorEl(event.currentTarget);
    setSelectedAppeal(appeal);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAppeal(null);
  };

  const handleEdit = () => {
    if (selectedAppeal) {
      navigate(`/appeals/${selectedAppeal.id}`);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    console.log('handleDelete called with selectedAppeal:', selectedAppeal);
    setAppealToDelete(selectedAppeal); // Store the appeal to delete
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDelete = async () => {
    console.log('confirmDelete called with appealToDelete:', appealToDelete);
    if (!appealToDelete) {
      console.error('No appeal to delete');
      return;
    }

    try {
      setDeleting(true);
      setError(null); // Clear any previous errors

      console.log('Making delete request to:', `/documents/minimal-appeals/${appealToDelete.id}/`);
      const response = await api.delete(`/documents/minimal-appeals/${appealToDelete.id}/`);
      console.log('Delete response:', response);

      await loadAppeals(); // Refresh the list
      setDeleteDialogOpen(false);
      setAppealToDelete(null); // Clear the appeal to delete
      setSelectedAppeal(null);

      console.log('Appeal deleted successfully');

    } catch (err) {
      console.error('Delete error:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to delete appeal';
      setError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = async () => {
    if (selectedAppeal && selectedAppeal.letter) {
      try {
        const response = await api.get(`/documents/minimal-appeals/${selectedAppeal.id}/download/`, {
          responseType: 'blob',
        });
        const contentDisposition = response.headers['content-disposition'] || '';
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        const filename = filenameMatch?.[1] || `appeal-${selectedAppeal.patient_name}-${new Date().toISOString().split('T')[0]}.pdf`;
        const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        setError('Failed to download appeal');
      }
    }
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'success';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return <CheckCircleIcon size={16} />;
      case 'draft': return <ScheduleIcon size={16} />;
      default: return <ScheduleIcon size={16} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const renderStatsCards = () => {
    const draftCount = appeals.filter(appeal => appeal.status === 'draft').length;
    const sentCount = appeals.filter(appeal => appeal.status === 'sent').length;

    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Priority: Urgent appeals */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ bgcolor: draftCount > 0 ? 'warning.light' : 'grey.50', border: draftCount > 0 ? 2 : 0, borderColor: 'warning.main' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" fontWeight={700} color={draftCount > 0 ? 'warning.dark' : 'text.secondary'}>
                {draftCount}
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                Needs Attention
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Draft appeals to complete
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Secondary: Total */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ bgcolor: 'grey.50' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" fontWeight={700} color="text.secondary">
                {appeals.length}
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                Total Appeals
              </Typography>
              <Typography variant="caption" color="text.secondary">
                All time
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Success metric */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ bgcolor: 'grey.50' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" fontWeight={700} color="success.main">
                {sentCount}
              </Typography>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                Submitted
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Successfully sent
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderAppealsTable = () => {
    if (appeals.length === 0) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <DescriptionIcon size={60} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No Appeals Found
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Get started by creating your first appeal using the minimal upload workflow.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon size={16} />}
              onClick={onNewAppeal}
              size="large"
            >
              Create First Appeal
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <TableContainer component={Paper} sx={TABLE_CONTAINER_SX}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD_CELL_SX}>Patient Name</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Payer</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Claim Number</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Documents</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Created</TableCell>
              <TableCell sx={HEAD_CELL_SX}>Updated</TableCell>
              <TableCell align="right" sx={{ ...HEAD_CELL_SX, textAlign: 'right' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {appeals.map((appeal) => (
              <TableRow key={appeal.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {appeal.patient_name || 'Unnamed Patient'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {appeal.payer_name || 'Not specified'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {appeal.claim_number || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={getStatusIcon(appeal.status)}
                    label={appeal.status?.charAt(0).toUpperCase() + appeal.status?.slice(1) || 'Draft'}
                    color={getStatusColor(appeal.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {appeal.documents?.length || 0} files
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(appeal.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(appeal.updated_at)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(e) => handleMenuClick(e, appeal)}
                    size="small"
                  >
                    <MoreVertIcon size={16} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Appeals Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and track your insurance appeal letters
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon size={16} />}
          onClick={onNewAppeal}
          size="large"
          sx={{ minHeight: 48 }}
        >
          Create Appeal
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {renderStatsCards()}
      {renderAppealsTable()}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon size={16} style={{marginRight: 4}} />
          Edit Appeal
        </MenuItem>
        {selectedAppeal?.letter && (
          <MenuItem onClick={handleDownload}>
            <GetAppIcon size={16} style={{marginRight: 4}} />
            Download PDF
          </MenuItem>
        )}
        <MenuItem onClick={() => navigate(`/appeals/${selectedAppeal?.id}`)}>
          <VisibilityIcon size={16} style={{marginRight: 4}} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon size={16} style={{marginRight: 4}} />
          Delete Appeal
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => {
        setDeleteDialogOpen(false);
        setAppealToDelete(null);
      }}>
        <DialogTitle>Delete Appeal</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the appeal for "{appealToDelete?.patient_name || 'Unnamed Patient'}"?
            This action cannot be undone.
          </DialogContentText>
          {appealToDelete && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Appeal ID: {appealToDelete.id}<br/>
              Payer: {appealToDelete.payer_name || 'Not specified'}<br/>
              Created: {appealToDelete.created_at ? new Date(appealToDelete.created_at).toLocaleDateString() : 'Unknown'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setAppealToDelete(null);
          }}>Cancel</Button>
          <Button
            onClick={() => {
              console.log('Delete button clicked!');
              confirmDelete();
            }}
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppealsDashboard;
